import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import CountUp from "react-countup";
import {
    Alert,
    alpha,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { getApiBaseUrl } from "../../config/api";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import GradingIcon from "@mui/icons-material/GradeOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SeeNotice from "../../components/SeeNotice";
import { getClassStudents } from "../../redux/sclassRelated/sclassHandle";
import { getTeacherStats } from "../../redux/teacherRelated/teacherToolsHandle";
import { useTeacherTeaching } from "../../hooks/useTeacherTeaching";
import { getSubjectId } from "../../utils/teacherSubjects";
import { useFormTeacherHomeroom } from "../../hooks/useFormTeacherHomeroom";
import { useHomeroomTeacherTerm } from "../../hooks/useHomeroomTeacherTerm";

function StatCard({ title, value, hint, icon, Icon, color = "primary" }) {
    const IconComponent = icon ?? Icon;
    if (!IconComponent) return null;

    return (
        <Card
            elevation={0}
            sx={{
                height: "100%",
                borderRadius: 2.5,
                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                bgcolor: "background.paper",
                transition: (t) => t.transitions.create(["box-shadow", "border-color"], { duration: 180 }),
                "&:hover": {
                    borderColor: (t) => alpha(t.palette[color]?.main ?? t.palette.primary.main, 0.35),
                    boxShadow: (t) => `0 6px 20px ${alpha(t.palette.common.black, 0.06)}`,
                },
            }}
        >
            <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="overline"
                            color="text.secondary"
                            sx={{ fontWeight: 600, letterSpacing: 0.6, lineHeight: 1.3 }}
                        >
                            {title}
                        </Typography>
                        <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, lineHeight: 1.1 }}>
                            <CountUp end={value} duration={1.8} preserveValue />
                        </Typography>
                        {hint ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                                {hint}
                            </Typography>
                        ) : null}
                    </Box>
                    <Avatar
                        variant="rounded"
                        sx={{
                            width: 44,
                            height: 44,
                            bgcolor: (t) => alpha(t.palette[color]?.main ?? t.palette.primary.main, 0.1),
                            color: (t) => t.palette[color]?.main ?? t.palette.primary.main,
                        }}
                    >
                        <IconComponent fontSize="small" />
                    </Avatar>
                </Stack>
            </CardContent>
        </Card>
    );
}

function QuickLinkCard({ to, label, description, icon, Icon, color = "primary" }) {
    const IconComponent = icon ?? Icon;
    if (!IconComponent) return null;

    return (
        <Card
            component={RouterLink}
            to={to}
            elevation={0}
            sx={(t) => {
                const main = t.palette[color]?.main ?? t.palette.primary.main;
                return {
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    textDecoration: "none",
                    color: "inherit",
                    borderRadius: 2.5,
                    overflow: "hidden",
                    border: `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    bgcolor: "background.paper",
                    transition: t.transitions.create(["transform", "box-shadow", "border-color"], { duration: 200 }),
                    "&:hover": {
                        transform: "translateY(-4px)",
                        borderColor: alpha(main, 0.45),
                        boxShadow: `0 12px 32px ${alpha(main, 0.18)}`,
                        "& .quick-access-arrow": {
                            transform: "translateX(4px)",
                            color: main,
                        },
                    },
                };
            }}
        >
            <Box
                sx={(t) => ({
                    height: 4,
                    bgcolor: t.palette[color]?.main ?? t.palette.primary.main,
                })}
            />
            <CardContent
                sx={{
                    flex: 1,
                    p: 2.25,
                    display: "flex",
                    flexDirection: "column",
                    "&:last-child": { pb: 2.25 },
                }}
            >
                <Avatar
                    variant="rounded"
                    sx={(t) => ({
                        width: 52,
                        height: 52,
                        mb: 1.5,
                        bgcolor: alpha(t.palette[color]?.main ?? t.palette.primary.main, 0.12),
                        color: t.palette[color]?.main ?? t.palette.primary.main,
                    })}
                >
                    <IconComponent />
                </Avatar>
                <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.25, mb: 0.5 }}>
                    {label}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flex: 1, fontSize: "0.8125rem", lineHeight: 1.45 }}
                >
                    {description}
                </Typography>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ mt: 2, pt: 1.5, borderTop: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}` }}
                >
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                        Open
                    </Typography>
                    <ArrowForwardIcon
                        className="quick-access-arrow"
                        sx={{
                            fontSize: 16,
                            color: "text.disabled",
                            transition: (t) => t.transitions.create(["transform", "color"], { duration: 200 }),
                        }}
                    />
                </Stack>
            </CardContent>
        </Card>
    );
}

const TeacherHomePage = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);
    const { sclassStudents } = useSelector((state) => state.sclass);
    const { teacherStats } = useSelector((state) => state.teacherTools);

    const teacherId = currentUser?._id;
    const { subjects, activeClassId: subjectClassId, className, subjectName } = useTeacherTeaching();
    const { hasHomeroomClass, homeroomClassId, homeroomClassName, homeroomLabel, showMyClass } =
        useFormTeacherHomeroom();
    const homeroomTerm = useHomeroomTeacherTerm();
    const [pendingMarksReview, setPendingMarksReview] = useState(0);

    const classID = subjectClassId || homeroomClassId;
    const displayClassName = className && className !== "—" ? className : homeroomClassName;
    const schoolName = currentUser?.school?.schoolName;

    useEffect(() => {
        if (classID) dispatch(getClassStudents(classID));
        if (teacherId) dispatch(getTeacherStats(teacherId));
    }, [dispatch, classID, teacherId]);

    useEffect(() => {
        if (!hasHomeroomClass || !teacherId) {
            setPendingMarksReview(0);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(
                    `${getApiBaseUrl()}/ClassResults/form-teacher/${teacherId}/subjects`,
                );
                if (cancelled) return;
                const list = Array.isArray(data) ? data : [];
                setPendingMarksReview(list.filter((s) => s.status === "Submitted").length);
            } catch {
                if (!cancelled) setPendingMarksReview(0);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [hasHomeroomClass, teacherId]);

    const numberOfStudents = Array.isArray(sclassStudents) ? sclassStudents.length : 0;
    const leaveBalance = teacherStats?.leaveBalance ?? 0;
    const leaveColor = leaveBalance > 5 ? "success" : leaveBalance >= 1 ? "warning" : "error";

    const stats = useMemo(
        () => [
            {
                key: "students",
                title: "Class students",
                value: teacherStats?.studentCount ?? numberOfStudents,
                hint: "Enrolled in your class",
                Icon: PeopleAltOutlinedIcon,
                color: "primary",
            },
            {
                key: "marks",
                title: "Marks recorded",
                value: teacherStats?.marksRecorded ?? 0,
                hint: "Exam entries for your subject",
                Icon: GradingIcon,
                color: "info",
            },
            {
                key: "assignments",
                title: "Assignments",
                value: teacherStats?.assignmentsPosted ?? 0,
                hint: "Posted this term",
                Icon: AssignmentOutlinedIcon,
                color: "secondary",
            },
            {
                key: "lessons",
                title: "Lesson plans",
                value: teacherStats?.lessonsThisMonth ?? 0,
                hint: "Logged this month",
                Icon: EventNoteOutlinedIcon,
                color: "info",
            },
            {
                key: "leave",
                title: "Leave balance",
                value: leaveBalance,
                hint: "Annual days remaining",
                Icon: EventBusyOutlinedIcon,
                color: leaveColor,
            },
            {
                key: "schemes",
                title: "Schemes submitted",
                value: teacherStats?.schemesSubmitted ?? 0,
                hint: "This academic year",
                Icon: MenuBookOutlinedIcon,
                color: "primary",
            },
        ],
        [teacherStats, numberOfStudents, leaveBalance, leaveColor]
    );

    const quickLinks = useMemo(() => {
        const links = [];
        if (showMyClass && hasHomeroomClass) {
            links.unshift({
                to: "/Teacher/form-teacher-results",
                label: pendingMarksReview > 0 ? `Review subject marks (${pendingMarksReview})` : "Review subject marks",
                description: "Approve marks submitted by subject teachers, then send to director",
                Icon: GradingIcon,
                color: pendingMarksReview > 0 ? "warning" : "primary",
            });
            links.push({
                to: "/Teacher/my-class",
                label: "My class",
                description: `Everything for ${homeroomLabel || "your class"} — students, enrollment, attendance, report cards`,
                Icon: ClassOutlinedIcon,
                color: "primary",
            });
        } else if (showMyClass) {
            links.push({
                to: "/Teacher/my-class",
                label: "My class (setup)",
                description: "Your director must link you to a class — open for instructions",
                Icon: ClassOutlinedIcon,
                color: "warning",
            });
        }
        links.push(
            {
                to: hasHomeroomClass ? "/Teacher/my-class/students" : "/Teacher/class",
                label: hasHomeroomClass ? "View my students" : "Class roster",
                description: "See students in your class",
                Icon: PeopleAltOutlinedIcon,
                color: "primary",
            },
            {
                to: "/Teacher/marks-entry",
                label: "Upload test results",
                description: "Enter or import marks for your class",
                Icon: GradingIcon,
                color: "info",
            },
            {
                to: "/Teacher/attendance-entry",
                label: "Record attendance",
                description: "Submit attendance for admin approval",
                Icon: FactCheckOutlinedIcon,
                color: "success",
            },
            {
                to: "/Teacher/schemes",
                label: "Scheme of work",
                description: "Plan and submit curriculum",
                Icon: MenuBookOutlinedIcon,
                color: "primary",
            },
            {
                to: "/Teacher/lessons",
                label: "Lesson plans",
                description: "Daily teaching notes",
                Icon: EventNoteOutlinedIcon,
                color: "info",
            },
            {
                to: "/Teacher/assignments",
                label: "Assignments",
                description: "Homework and assessments",
                Icon: AssignmentOutlinedIcon,
                color: "secondary",
            },
            {
                to: "/Teacher/resources",
                label: "Class resources",
                description: "Past papers, notes & books",
                Icon: FolderSharedOutlinedIcon,
                color: "primary",
            },
            {
                to: "/Teacher/leave",
                label: "Leave application",
                description: "Request time off",
                Icon: EventBusyOutlinedIcon,
                color: "warning",
            },
        );
        return links;
    }, [showMyClass, hasHomeroomClass, homeroomLabel, pendingMarksReview]);

    return (
        <Box
            sx={{
                minHeight: "100%",
                pb: 4,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[100]} 24%)`
                        : alpha(t.palette.common.black, 0.15),
            }}
        >
            <Container maxWidth="xl" sx={{ pt: { xs: 2, sm: 3 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, sm: 3 },
                        mb: 3,
                        borderRadius: 3,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}`,
                        background: (t) =>
                            `linear-gradient(120deg, ${t.palette.grey[900]} 0%, ${t.palette.primary.dark} 55%, ${t.palette.primary.main} 100%)`,
                        color: "common.white",
                    }}
                >
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ md: "center" }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                                sx={{
                                    width: 52,
                                    height: 52,
                                    bgcolor: alpha("#fff", 0.14),
                                    fontWeight: 700,
                                    fontSize: "1.25rem",
                                }}
                            >
                                {(currentUser?.name || "T").charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1.1 }}>
                                    Teacher workspace
                                </Typography>
                                <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                                    Welcome back, {currentUser?.name || "Teacher"}
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
                                    {hasHomeroomClass && homeroomLabel ? (
                                        <Chip
                                            size="small"
                                            label={`${homeroomTerm.chipPrefix}: ${homeroomLabel}`}
                                            sx={{ bgcolor: alpha("#fff", 0.12), color: "inherit", fontWeight: 600 }}
                                        />
                                    ) : null}
                                    {displayClassName ? (
                                        <Chip
                                            size="small"
                                            label={`Class: ${displayClassName}`}
                                            sx={{ bgcolor: alpha("#fff", 0.12), color: "inherit", fontWeight: 600 }}
                                        />
                                    ) : null}
                                    {subjects.length > 0
                                        ? subjects.map((sub) => {
                                              const cls = sub.sclassName?.sclassName;
                                              const label = cls ? `${sub.subName} · ${cls}` : sub.subName;
                                              return (
                                                  <Chip
                                                      key={getSubjectId(sub)}
                                                      size="small"
                                                      label={label}
                                                      sx={{
                                                          bgcolor: alpha("#fff", 0.12),
                                                          color: "inherit",
                                                          fontWeight: 600,
                                                      }}
                                                  />
                                              );
                                          })
                                        : subjectName ? (
                                              <Chip
                                                  size="small"
                                                  label={`Subject: ${subjectName}`}
                                                  sx={{
                                                      bgcolor: alpha("#fff", 0.12),
                                                      color: "inherit",
                                                      fontWeight: 600,
                                                  }}
                                              />
                                          ) : null}
                                    {schoolName ? (
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={schoolName}
                                            sx={{ borderColor: alpha("#fff", 0.35), color: "inherit" }}
                                        />
                                    ) : null}
                                </Stack>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button
                                component={RouterLink}
                                to="/Teacher/class"
                                variant="contained"
                                sx={{
                                    bgcolor: "common.white",
                                    color: "primary.dark",
                                    fontWeight: 700,
                                    "&:hover": { bgcolor: alpha("#fff", 0.9) },
                                }}
                            >
                                Open class roster
                            </Button>
                            <Button
                                component={RouterLink}
                                to="/Teacher/profile"
                                variant="outlined"
                                sx={{ borderColor: alpha("#fff", 0.5), color: "common.white" }}
                            >
                                My profile
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                {hasHomeroomClass && pendingMarksReview > 0 ? (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2, borderRadius: 2 }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                component={RouterLink}
                                to="/Teacher/form-teacher-results"
                            >
                                Review now
                            </Button>
                        }
                    >
                        <strong>
                            {pendingMarksReview} subject mark batch{pendingMarksReview !== 1 ? "es" : ""}
                        </strong>{" "}
                        waiting for your approval as {homeroomTerm.theRole}. Open{" "}
                        <strong>Review subject marks</strong> (My class → Class results) to accept them before
                        sending to the director.
                    </Alert>
                ) : null}

                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.3 }}>
                    Performance overview
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {stats.map((card) => (
                        <Grid item xs={12} sm={6} md={4} lg={2} key={card.key}>
                            <StatCard {...card} />
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12} lg={7}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, sm: 2.5 },
                                borderRadius: 3,
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Box>
                                    <Typography variant="h6" fontWeight={800}>
                                        Quick access
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Jump to your most-used teaching tools
                                    </Typography>
                                </Box>
                                <Chip
                                    size="small"
                                    label={`${quickLinks.length} shortcuts`}
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                            </Stack>
                            <Grid container spacing={2}>
                                {quickLinks.map((link) => (
                                    <Grid item xs={12} sm={6} md={4} key={link.to}>
                                        <QuickLinkCard {...link} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} lg={5}>
                        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.3 }}>
                            School notices
                        </Typography>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2.5,
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                minHeight: 280,
                            }}
                        >
                            <SeeNotice />
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default TeacherHomePage;
